import { ReportRepository } from "../interfaces";
import { ClientIncomeSummary, InvoiceStatus, ReportBasis, TaxYearSummary } from "../types";
import { TaxYearConfig, calculateMileageAllowance } from "../../pages/Reports/taxYearConfig";
import { InMemoryStore } from "./store";
import { roundToPence } from "../../utils/money";

export class InMemoryReportRepository implements ReportRepository {
  constructor(private store: InMemoryStore) {}

  async getTaxYearSummary(taxYear: TaxYearConfig, basis: ReportBasis): Promise<TaxYearSummary> {
    const { start, end } = taxYear;

    const srsInDateRange = this.store.servicesRendered.filter((sr) => sr.date >= start && sr.date <= end);

    let totalMilesDriven = 0;
    for (const sr of srsInDateRange) {
      totalMilesDriven += sr.mileage ?? 0;
    }

    const outstandingInvoices = this.store.invoices.filter(
      (inv) => inv.status === InvoiceStatus.SENT && inv.endDate && inv.endDate >= start && inv.endDate <= end
    );

    const activeSrs = basis === "cash" ? this.getCashBasisSrs(taxYear) : this.getAccrualsBasisSrs(taxYear, srsInDateRange);

    const clientMap = new Map<string, ClientIncomeSummary>();
    let totalServiceIncome = 0;
    let totalMileageBilled = 0;

    for (const sr of activeSrs) {
      const client = this.store.clients.find((c) => c.id === sr.clientId);
      if (!client) continue;
      const service = this.store.services.find((s) => s.id === sr.serviceId);
      const cost = (sr.serviceCost ?? service?.cost ?? 0) * sr.quantity;
      // Round once at extraction so every downstream usage (totalMileageBilled and the
      // per-client breakdown) rounds each activity's mileage cost individually before it's
      // added into any total, rather than summing raw fractional values and rounding once.
      const mileageCost = roundToPence(sr.mileageCost ?? 0);

      totalServiceIncome += cost;
      totalMileageBilled += mileageCost;

      const existing = clientMap.get(client.id);
      if (existing) {
        existing.serviceIncome += cost;
        existing.mileageBilled += mileageCost;
      } else {
        clientMap.set(client.id, {
          clientId: client.id,
          clientName: `${client.name} ${client.surname}`.trim(),
          serviceIncome: cost,
          mileageBilled: mileageCost,
        });
      }
    }

    return {
      serviceIncome: totalServiceIncome,
      mileageBilled: totalMileageBilled,
      totalMilesDriven,
      mileageAllowance: calculateMileageAllowance(totalMilesDriven),
      perClient: Array.from(clientMap.values()),
      outstandingInvoices,
    };
  }

  private getAccrualsBasisSrs(taxYear: TaxYearConfig, srsInDateRange: InMemoryStore["servicesRendered"]) {
    const { start, end } = taxYear;

    const excludedIds = new Set(
      this.store.invoices
        .filter(
          (inv) =>
            (inv.status === InvoiceStatus.VOIDED || inv.status === InvoiceStatus.CANCELLED) &&
            inv.startDate &&
            inv.startDate <= end &&
            inv.endDate &&
            inv.endDate >= start
        )
        .map((inv) => inv.id)
    );

    return srsInDateRange.filter((sr) => !sr.invoiceID || !excludedIds.has(sr.invoiceID));
  }

  private getCashBasisSrs(taxYear: TaxYearConfig) {
    const { start, end } = taxYear;

    const paidIdsInRange = new Set(
      this.store.invoices
        .filter((inv) => inv.status === InvoiceStatus.PAID && inv.paidAt && inv.paidAt >= start && inv.paidAt <= end)
        .map((inv) => inv.id)
    );

    return this.store.servicesRendered.filter((sr) => !!sr.invoiceID && paidIdsInRange.has(sr.invoiceID));
  }
}
